<template>
  <sub-page
    v-if="returnedSalaryEmployees.length"
    title="Returned Salaries"
    subtitle="Salaries returned to you (less any deductions)"
  >
    <header class="d-flex flex-column flex-sm-row align-items-end mb-3">
      <export-button
        :file-name="exportFilename"
        :export-data="returnedSalaryEmployees"
        :export-file-rows="exportFileRows"
        :export-headers="EXPORT_HEADERS"
      />
    </header>

    <sme-detailed-table
      :fields="FIELDS"
      :items="returnedSalaryEmployees"
      tbody-tr-class="sme-detailed-table__row--link"
      sticky-header
      @row-clicked="item => $router.push({ name: 'employee', params: { employeeId: item.employee_id } })"
    >
      <template #cell(employee_name)="data">
        <div class="sme-detailed-table__cell sme-detailed-table__cell--wide">
          <strong class="sme-detailed-table__label sme-detailed-table__label--large">
            {{ data.item.employee_name }}
          </strong>
          <span class="sme-detailed-table__value sme-detailed-table__value--primary">
            {{ getEmployeeCodeOrExternalId(data.item.employee_code, data.item.external_ids, State.state.company) }}
          </span>
        </div>
      </template>
      <template #cell(pay_schedule_name)="data">
        <div class="sme-detailed-table__cell">
          <span class="sme-detailed-table__label">Pay schedule</span>
          <router-link
            :to="{ name: 'pay-schedule', params: { payScheduleId: data.item.pay_schedule_id } }"
            class="sme-detailed-table__value"
          >
            <strong>{{ data.item.pay_schedule_name }}</strong>
          </router-link>
        </div>
      </template>
    </sme-detailed-table>
  </sub-page>
</template>

<script setup>
import orderBy from 'lodash/orderBy';
import moment from 'moment/moment';
import { computed } from 'vue';
import { useRouter } from 'vue-router/composables';
import ExportButton from '@/components/ExportButton.vue';
import SubPage from '@/components/SubPage.vue';
import SmeDetailedTable from '@/components/atoms/SmeDetailedTable.vue';
import State from '@/state/State';
import { toCurrency } from '@/utils';
import { toFormattedDate } from '@/utils/date';
import usePayschedulesReport from '@/state/composables/usePayschedulesReport';
import { getEmployeeCodeOrExternalId, formatEmployeeCodeOrExternalIdLabel } from '@/utils/common';

const $router = useRouter();
const { payscheduleReports } = usePayschedulesReport();

const returnedSalaryEmployees = computed(() => {
  return orderBy(
    payscheduleReports.value.flatMap(report => {
      report.returnedSalaries.forEach(employee => (employee.pay_schedule_name = report.payschedule.name));
      return report.returnedSalaries;
    }),
    'returned_at',
    'desc',
  );
});

const FIELDS = [
  {
    key: 'employee_name',
    label: 'Name',
  },
  {
    key: 'pay_schedule_name',
    label: 'Pay schedule',
  },
  {
    key: 'returned_at',
    label: 'Date',
    formatter: value => toFormattedDate(value),
  },
  {
    key: 'returned_amount',
    label: 'Amount',
    formatter: value => toCurrency(value),
  },
  {
    key: 'return_reason',
    label: 'Reason',
  },
];

const EXPORT_HEADERS = computed(() => {
  const externalIdKey = State.state.company?.properties?.portal?.external_id_for_portal;
  return [formatEmployeeCodeOrExternalIdLabel(externalIdKey), ...FIELDS.map(field => field.label)];
});

const exportFilename = computed(() => {
  const today = moment().format('YYYY-MM-DD');
  let companyName = '';
  if (State.state.company.name) {
    companyName = State.state.company.name.toLowerCase()?.replace(/ /g, '-');
  }

  return `payroll-returned-salaries-list-${companyName}-${today}`;
});

const exportFileRows = item => [
  getEmployeeCodeOrExternalId(item.employee_code, item.external_ids, State.state.company),
  ...FIELDS.map(field => (field.formatter ? field.formatter(item[field.key], field.key, item) : item[field.key])),
];
</script>
